// Iter-195p/s — Visual badge shown on any invoice/mission/client row whose
// automatic payment reminders are currently paused. The badge label
// indicates the SCOPE of the pause:
//   • "Pause"    → both milestones AND overdue (or legacy paused=true)
//   • "Pause M"  → milestones only
//   • "Pause OD" → overdue only
//
// Clicking the badge (when `canReactivate`) opens a confirmation popping
// the option to fully reactivate or change scope.
import React, { useState } from "react";
import { Pause, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const KIND_PATH = {
  invoice: (id) => `/invoices/${id}/reminders-paused`,
  mission: (id) => `/missions/${id}/reminders-paused`,
  client:  (id) => `/clients/${id}/reminders-paused`,
};
const KIND_LABEL = {
  invoice: "cette facture",
  mission: "cette mission (toutes ses factures)",
  client:  "ce client (toutes ses missions)",
};

// Iter-195s — derive the effective scope from the row's two granular fields
// (with back-compat for the legacy `payment_reminders_paused` boolean).
export function scopeOf(row) {
  if (!row) return "NONE";
  const legacy = row.payment_reminders_paused === true;
  const ms = legacy || row.payment_reminders_paused_milestones === true;
  const od = legacy || row.payment_reminders_paused_overdue === true;
  if (ms && od) return "ALL";
  if (ms) return "MILESTONE";
  if (od) return "OVERDUE";
  return "NONE";
}

const SCOPE_META = {
  ALL:       { label: "Pause",    full: "Milestones + Dépassement" },
  MILESTONE: { label: "Pause M",  full: "Milestones uniquement" },
  OVERDUE:   { label: "Pause OD", full: "Dépassement uniquement" },
};

export default function RemindersPausedBadge({
  paused, scope, row, kind, id, canReactivate = false, allowInitialPause = false,
  onChanged, size = "sm",
}) {
  const [busy, setBusy] = useState(false);
  // Iter-195s — accept either an explicit `scope` prop OR a `row` object
  // we can derive from. Falls back to the legacy `paused` boolean.
  const eff = scope || (row ? scopeOf(row) : (paused ? "ALL" : "NONE"));

  const change = async (newPaused, newScope) => {
    setBusy(true);
    try {
      const body = newPaused ? { paused: true, scope: newScope } : { paused: false };
      await api.patch(KIND_PATH[kind](id), body);
      toast.success(newPaused
        ? `Relances mises à jour : ${SCOPE_META[newScope]?.full || newScope}`
        : "Relances automatiques réactivées");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };

  // Iter-195t — When not paused, optionally surface a small "Pause" entry
  // point so the CEO can initiate the pause directly from the list with
  // scope choice (Milestones / Overdue / Both). Mirrors the per-invoice UX.
  if (eff === "NONE") {
    if (!allowInitialPause) return null;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={stop}
            data-testid={`reminders-pause-trigger-${kind}-${id}`}
            title={`Mettre en pause les relances automatiques sur ${KIND_LABEL[kind]}`}
            className={`inline-flex items-center gap-1 ${
              size === "xs" ? "text-[9px] px-1 py-px h-4" : "text-[10px] px-1.5 py-0.5"
            } font-mono rounded border bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50 hover:text-zinc-900`}
          >
            <Pause size={size === "xs" ? 8 : 9} /> Pause
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={stop}
          onPointerDown={stop}
        >
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); change(true, "MILESTONE"); }}
            data-testid={`reminders-pause-set-milestone-${kind}-${id}`}
          >
            Pause Milestones uniquement
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); change(true, "OVERDUE"); }}
            data-testid={`reminders-pause-set-overdue-${kind}-${id}`}
          >
            Pause Dépassement uniquement
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); change(true, "ALL"); }}
            data-testid={`reminders-pause-set-all-${kind}-${id}`}
          >
            Pause complète (les deux)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const meta = SCOPE_META[eff] || SCOPE_META.ALL;
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const pill = (
    <span
      data-testid={`reminders-paused-badge-${kind}-${id}`}
      data-scope={eff}
      onClick={canReactivate ? stop : undefined}
      className={`inline-flex items-center gap-1 ${
        size === "xs" ? "text-[9px] px-1 py-px h-4" : "text-[10px] px-1.5 py-0.5"
      } font-mono rounded border bg-amber-50 text-amber-900 border-amber-300 ${
        canReactivate ? "cursor-pointer hover:bg-amber-100" : ""
      }`}
      title={canReactivate
        ? `Relances auto en pause (${meta.full}) — cliquer pour changer ou réactiver`
        : `Relances en pause : ${meta.full} sur ${KIND_LABEL[kind]}`}
    >
      <Pause size={size === "xs" ? 8 : 9} /> {meta.label}
    </span>
  );

  if (!canReactivate) return pill;

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{pill}</AlertDialogTrigger>
      <AlertDialogContent data-testid={`reminders-paused-confirm-${kind}-${id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Modifier la mise en pause des relances</AlertDialogTitle>
          <AlertDialogDescription>
            Choisissez la portée de la pause sur {KIND_LABEL[kind]}, ou réactivez complètement les relances.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid grid-cols-1 gap-2 text-sm">
          {["MILESTONE", "OVERDUE", "ALL"].map((s) => (
            <button
              key={s}
              onClick={(e) => { e.preventDefault(); change(true, s); }}
              disabled={busy}
              data-testid={`reminders-paused-set-${s.toLowerCase()}-${kind}-${id}`}
              className={`text-left px-3 py-2 rounded-md border text-xs hover:bg-zinc-50 ${
                eff === s ? "border-zinc-950 bg-zinc-50 font-semibold" : "border-zinc-200"
              }`}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s}</div>
              <div>
                {s === "MILESTONE" && "Pause des relances avant échéance (milestones J+20, J+25…)"}
                {s === "OVERDUE" && "Pause de l'email d'escalade CEO en cas de dépassement"}
                {s === "ALL" && "Pause complète — aucune relance automatique ne part"}
              </div>
            </button>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); change(false); }}
            disabled={busy}
            data-testid={`reminders-paused-confirm-yes-${kind}-${id}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
            Réactiver toutes les relances
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
